import {
  Checkbox,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { GridRowData } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DropResult,
} from 'react-beautiful-dnd';
import { v4 as uuidv4 } from 'uuid';
import { IColDef } from './DataTable';

const DragDataTable: React.FC<{
  rows: any[];
  columns: IColDef[];
  onDragEnd: (draggedId, currPos, tarPos) => void;
  onDragMouseDown: () => void;
  onDragMouseUp: () => void;
  setSelected: (params: GridRowData[]) => void;
  selected: GridRowData[];
  enableSelectAll: boolean;
  openArr?: boolean[];
  // slideComponent?: React.FC<{ word: string }>;
  // SlideComponent?: React.ReactNode;
  SlideComponent?: any;
  slideComponentProps?: any;
}> = ({
  rows,
  columns,
  onDragEnd,
  onDragMouseDown,
  onDragMouseUp,
  setSelected,
  selected,
  enableSelectAll,
  openArr,
  SlideComponent,
  slideComponentProps,
}) => {
  const [localItems, setLocalItems] = useState<Array<Record<string, any>>>(
    rows
  );
  useEffect(() => {
    setLocalItems(rows);
  }, [rows]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    const draggedItem = rows.find((el) => el.id === result?.draggableId);
    const currentPosition = draggedItem?.order;
    const destinationItem = localItems[result.destination.index];
    const targetPosition = Math.max(
      destinationItem?.order,
      Number(result?.destination?.index) + 1
    );
    onDragEnd(draggedItem.id, currentPosition, targetPosition);
  };

  const colKeys = columns.map((col) => col.field);

  const renderHelper = (columns: IColDef[], item: any, keyName: string) => {
    const col = columns.find((col) => col.field === keyName);
    if (col?.renderCell) {
      return col?.field === 'id'
        ? col.renderCell(item[keyName])
        : col.renderCell(item);
    } else if (col?.valueFormatter) {
      return col.valueFormatter(item);
    } else {
      return item[keyName];
    }
  };

  return (
    <TableContainer>
      <Table>
        <colgroup>
          {columns.map((col) => (
            <col
              style={{ width: col?.dragTableWidth ?? '10%' }}
              key={col.field}
            />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            {columns.map((col) =>
              col.field === 'check' ? (
                <TableCell key={col.field} style={{ textAlign: 'center' }}>
                  <Checkbox
                    disabled={!enableSelectAll}
                    color="primary"
                    indeterminate={selected.length > 0}
                    onClick={() => {
                      selected.length ? setSelected([]) : setSelected(rows);
                    }}
                  />
                </TableCell>
              ) : (
                <TableCell key={col.field} align={col.headerAlign}>
                  {col.headerName}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        {localItems.length > 0 ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable" direction="vertical">
              {(droppableProvided: DroppableProvided) => (
                <TableBody
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {localItems.map((item: any, index: number) => {
                    return (
                      <Draggable
                        key={uuidv4()}
                        draggableId={String(item.id)}
                        index={index}
                      >
                        {(
                          draggableProvided: DraggableProvided,
                          snapshot: DraggableStateSnapshot
                        ) => {
                          return (
                            <>
                              <TableRow
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  background: snapshot.isDragging
                                    ? 'rgba(245,245,245, 0.75)'
                                    : 'none',
                                }}
                              >
                                {colKeys.map((keyName) =>
                                  keyName === 'order' ? (
                                    <TableCell key={keyName} align="center">
                                      <Tooltip title="Drag to reorder">
                                        <div
                                          {...draggableProvided.dragHandleProps}
                                        >
                                          <DragIndicatorIcon
                                            onMouseDown={() => {
                                              onDragMouseDown();
                                            }}
                                            onMouseUp={() => onDragMouseUp()}
                                          />
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                  ) : (
                                    <TableCell key={keyName}>
                                      {renderHelper(columns, item, keyName)}
                                    </TableCell>
                                  )
                                )}
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  style={{ paddingBottom: 0, paddingTop: 0 }}
                                  colSpan={6}
                                >
                                  <Collapse
                                    in={openArr?.[index] ?? false}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    {SlideComponent?.({
                                      index,
                                      rows,
                                      data: slideComponentProps,
                                    }) ?? null}
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </>
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={100} align="center">
                No Rows
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

export default DragDataTable;
